import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Container, Header, Segment, Dropdown, Statistic, Table, Label, Icon, Message, Loader, Button, Pagination, Input, Menu, Checkbox, List, Modal } from 'semantic-ui-react';
import { AuthStdFooter } from '../../utils/auth-std-footer';
import { apiClient } from '../../apiTools/apiClient';
import { config } from '../../constants/config';
import { fetchPrototypes, selectPrototypes } from '../../slices/litGroupPrototypeSlice';
import ManageCastModal from '../../components/prototype/ManageCastModal';
import VoiceDesignStudio from '../../components/talentAgency/VoiceDesignStudio';
import IngestPreviewPanel from '../../components/talentAgency/IngestPreviewPanel';
import CharacterContextTrigger from '../../components/characterContext/CharacterContextTrigger';
import CharacterLinesPanel from '../../components/prototype/CharacterLinesPanel';
import '../../styles/landing-pages.css';
import '../../styles/talent-agency.css';
import '../../styles/chevronToggle.css';

/**
 * TalentAgencyPage — the AAI Talent Agency surface (Casting Studio v1).
 * Pick a published Literary Group; see the casting tier funnel, the suggested voice-style roster
 * (with matched ElevenLabs library voice + cloning-sample status), and the cast. Reads the
 * derivation stamped on Characters + VoiceStyle roster in Neo4j.
 */
const TIER_COLOR = { narration: 'violet', lead: 'green', support: 'blue', walkon: 'grey', nonspeaking: 'black' };

// Detected-gender override — the real voice-driver for Auto-cast. Compact so the cell stays narrow.
const GENDER_OPTIONS = [
  { key: 'male', value: 'male', text: '♂ M' },
  { key: 'female', value: 'female', text: '♀ F' },
  { key: 'neutral', value: 'neutral', text: '⊘ N' },
  { key: 'unknown', value: 'unknown', text: '— ?' },
];

// Inline-editable detected archetype. Commits on blur / Enter when changed; Esc reverts.
const EditableArchetype = ({ value, onSave }) => {
  const [draft, setDraft] = useState(value || '');
  useEffect(() => { setDraft(value || ''); }, [value]);
  const commit = () => { const v = draft.trim(); if (v && v !== (value || '')) onSave(v); else setDraft(value || ''); };
  return (
    <Input
      transparent fluid size="mini"
      value={draft}
      placeholder="archetype…"
      onChange={(e, { value: v }) => setDraft(v)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.target.blur(); }
        else if (e.key === 'Escape') { setDraft(value || ''); e.target.blur(); }
      }}
      title="Edit the detected archetype — feeds Auto-cast (locks the row against re-derive)"
      style={{ color: '#555' }}
    />
  );
};

// Casting tiers ordered by prominence (drives the auto-cast panel's sort).
// Narrator first: it is the largest role in any book and the one most likely to be cast before
// anything else — often to a single voice across the whole series.
const TIER_RANK = { narration: -1, lead: 0, support: 1, walkon: 2 };

const TalentAgencyPage = () => {
  const dispatch = useDispatch();
  const prototypes = useSelector(selectPrototypes);
  const userState = useSelector((s) => s.user);
  const userData = userState?.data || userState?.profile?.data || userState || {};
  const userId = userData.userId || userData.id;
  const [litGroupUuid, setLitGroupUuid] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);   // transient success feedback (bulk apply/clear)
  const [auditionUuid, setAuditionUuid] = useState(null);
  const [auditionLoading, setAuditionLoading] = useState(false);
  const [auditionPaused, setAuditionPaused] = useState(false);
  const [previewUuid, setPreviewUuid] = useState(null);
  const [castPage, setCastPage] = useState(1);
  const [rosterPage, setRosterPage] = useState(1);
  const [castSearch, setCastSearch] = useState('');
  /**
   * Which cast rows have their dialogue open.
   *
   * ⭐ An ARRAY of uuids, not one — comparing two characters means having both open at once, and
   * that is the case this was built for: deciding a gender by reading what the person actually says.
   * Rows stay open across a page change, so paging back does not undo the reading.
   */
  const [linesOpen, setLinesOpen] = useState([]);
  const toggleLines = useCallback((uuid) => {
    setLinesOpen((prev) => (prev.includes(uuid) ? prev.filter((x) => x !== uuid) : [...prev, uuid]));
  }, []);
  const [rosterSearch, setRosterSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [archetypeFilter, setArchetypeFilter] = useState('');
  /**
   * ─── ⭐ BOOK FILTER, DEFAULTING TO ALL BOOKS ────────────────────────────────────────────────
   *
   * ⛔ WHY THIS EXISTS, in the developer's own words: *"I, the authoratori-ai developer, am
   * confused by the Casting Studio's Cast list — thinking it is showing me the current book, not
   * the LG set."* If the person who built the app misreads the scope of his own list, the list is
   * not labelled well enough.
   *
   * ⭐ AND THE FIX IS A SELECTOR, NOT A CAPTION. A filter that can be SET also states its own
   * scope: "All books" sitting in a dropdown says the list is group-wide far better than a label
   * above it, because it is the thing that would change if it were not.
   *
   * ⚠️ ALL BOOKS is the default on purpose — it is what this list has always shown, so the
   * control explains the existing behaviour rather than silently changing it. Same model as the
   * Green Room's filter, so the two rooms behave alike.
   */
  const CAST_ALL_BOOKS = '__all__';
  const [castBookFilter, setCastBookFilter] = useState(CAST_ALL_BOOKS);
  const [room, setRoom] = useState('studio');   // Agency "rooms": studio | voicedesign | greenroom
  const [designs, setDesigns] = useState([]);   // saved Voice Designs for the selected LitGroup
  // What casting this roster would COST, read before anything is spent. Fetched separately from the
  // summary because it calls ElevenLabs twice (listVoices + subscription usage) — both free reads,
  // but network, so a slow or failed account read must never delay or break the panel.
  const [estimate, setEstimate] = useState(null);
  const [estimateErr, setEstimateErr] = useState(null);
  const [designBusy, setDesignBusy] = useState(null);  // characterUuid or `style:<key>` mid-attach
  const [deriving, setDeriving] = useState(false);       // casting derivation in flight
  const [deriveProgress, setDeriveProgress] = useState(null); // { percent, message }
  const [castPanelOpen, setCastPanelOpen] = useState(false);  // inline "voice / re-voice cast" panel
  const [castSel, setCastSel] = useState(() => new Set());    // selected character uuids for bulk (re)voice
  const audioRef = useRef(null);
  const esRef = useRef(null);   // SSE EventSource for derivation progress
  const CAST_PAGE_SIZE = 25;
  const ROSTER_PAGE_SIZE = 10;

  useEffect(() => { dispatch(fetchPrototypes()); }, [dispatch]);
  // Any search/filter change returns to page 1 (else the current page can fall past the results).
  useEffect(() => { setCastPage(1); }, [castSearch, tierFilter, archetypeFilter, castBookFilter]);
  useEffect(() => { setRosterPage(1); }, [rosterSearch]);

  const stopAudio = () => {
    if (audioRef.current) { try { audioRef.current.pause(); } catch (_) { /* noop */ } audioRef.current = null; }
  };
  const resetAuditionState = () => { setAuditionUuid(null); setAuditionLoading(false); setAuditionPaused(false); };

  // The Audition — render the character's own line through its style's matched voice, and play it.
  const audition = async (uuid, name) => {
    try {
      stopAudio();
      setError(null);
      setAuditionUuid(uuid); setAuditionLoading(true); setAuditionPaused(false);
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const resp = await fetch(`${config.BASE_URL}/characters/${uuid}/audition`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) {
        let msg = 'Audition failed';
        try { msg = (await resp.json()).error || msg; } catch (_) { /* non-JSON */ }
        setError(`${name}: ${msg}`);
        resetAuditionState();
        return;
      }
      // The server returns a signed URL to the (freshly-rendered or cached) audio.
      const { audioUrl } = await resp.json();
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      audio.onended = () => { audioRef.current = null; resetAuditionState(); };
      audio.onerror = () => { audioRef.current = null; resetAuditionState(); };
      await audio.play();
      setAuditionLoading(false);
    } catch (e) {
      setError(`${name}: ${e.message || 'audition error'}`);
      resetAuditionState();
    }
  };

  // Pause / resume the current audition.
  const togglePauseAudition = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) { a.play(); setAuditionPaused(false); }
    else { a.pause(); setAuditionPaused(true); }
  };

  // Cancel — stop and reset the current audition.
  const cancelAudition = () => {
    const a = audioRef.current;
    if (a) { try { a.pause(); a.currentTime = 0; } catch (_) { /* noop */ } }
    audioRef.current = null;
    resetAuditionState();
  };

  // Voice preview — play the library voice's stock demo clip. Instant, client-side, no render/quota.
  // (What does this voice sound like? — vs. the audition's "hear this character say their line".)
  const previewVoice = (c) => {
    if (previewUuid === c.uuid) { // clicking again stops
      stopAudio(); setPreviewUuid(null); return;
    }
    if (!c.previewUrl) return;
    stopAudio();
    resetAuditionState();
    setPreviewUuid(c.uuid);
    const audio = new Audio(c.previewUrl);
    audioRef.current = audio;
    audio.onended = () => { audioRef.current = null; setPreviewUuid(null); };
    audio.onerror = () => { setPreviewUuid(null); };
    audio.play().catch(() => setPreviewUuid(null));
  };

  // Reassign a character's suggested style (the selection machinery). Updates locally; the next
  // ▶ auditions in the new voice (and caches it).
  const changeStyle = async (c, canonicalKey) => {
    if (!canonicalKey || canonicalKey === c.styleKey) return;
    try {
      setError(null);
      const resp = await apiClient.put(`/characters/${c.uuid}/style`, { canonicalKey });
      const { style, styleKey, hasVoice, previewUrl } = resp.data;
      // Stop any in-flight preview of the old voice so the next click plays the new style's clip.
      if (previewUuid === c.uuid) { stopAudio(); setPreviewUuid(null); }
      setData(prev => prev && ({
        ...prev,
        characters: prev.characters.map(x => x.uuid === c.uuid ? { ...x, style, styleKey, hasVoice, previewUrl } : x),
      }));
      // The style endpoint reports a narrower hasVoice than the casting read (it ignores an attached
      // design + the design-wins rule), so reconcile the row from the authoritative read — this is what
      // a manual refresh does, and it's what repopulates the Hear column correctly.
      refreshCasting();
    } catch (e) {
      setError(`${c.name}: ${e.response?.data?.error || e.message || 'style change failed'}`);
    }
  };

  // Override a character's detected signature (gender / archetype). The corrected gender is what an
  // Auto-cast re-roll pins the voice on; locks the row against re-derive. `patch` = { gender?, archetype? }.
  const changeSignature = async (c, patch) => {
    try {
      setError(null);
      const resp = await apiClient.put(`/characters/${c.uuid}/signature`, patch);
      const { gender, age, archetype, signatureLocked } = resp.data;
      setData(prev => prev && ({
        ...prev,
        characters: prev.characters.map(x => x.uuid === c.uuid ? { ...x, gender, age, archetype, signatureLocked } : x),
      }));
    } catch (e) {
      setError(`${c.name}: ${e.response?.data?.error || e.message || 'signature update failed'}`);
    }
  };

  // Published Literary Groups (the BC's live series) are the selectable options.
  const options = (prototypes || [])
    .filter(p => p.status === 'complete' && p.publishedLitGroupUuid)
    .map(p => ({ key: p.publishedLitGroupUuid, value: p.publishedLitGroupUuid, text: p.name }));

  // Load the LitGroup's saved Voice Designs (drives the attach dropdowns). Re-called when the
  // Voice Design Studio saves/deletes/renames one so the roster + cast dropdowns stay fresh.
  const fetchDesigns = async (uuid = litGroupUuid) => {
    if (!uuid) return;
    try { const r = await apiClient.get(`/litgroups/${uuid}/voice-designs`); setDesigns(r.data.designs || []); }
    catch { setDesigns([]); }
  };

  // ⭐ THE NUMBER THAT DECIDES THE SPEND, and the one the panel could not previously show.
  //
  // `reusable` vs `willBuy` is the reuse question. A fresh import mints brand-new Character nodes
  // with no VOICED_BY, so everything rests on findReusableVoice matching the existing pool BY NAME.
  // If it misses, a six-book cast goes 594 → 848 operations — a quarter of a month, unrecoverable —
  // and until now that was invisible until the cast screen, one navigation away from the decision.
  //
  // ⚠️ ADD/EDIT OPERATIONS ARE THE SCARCE RESOURCE, not characters and not slots: 1,040 a month,
  // two per voice, identical on Business and Scale, and not purchasable at any price.
  const fetchEstimate = async (uuid = litGroupUuid) => {
    if (!uuid) return;
    setEstimate(null); setEstimateErr(null);
    try {
      const r = await apiClient.post(`/litgroups/${uuid}/casting/auto-cast/estimate`, {});
      setEstimate(r.data || null);
    } catch (e) {
      setEstimateErr(e.response?.data?.error || e.message || 'estimate unavailable');
    }
  };

  const load = async (uuid) => {
    setLitGroupUuid(uuid); setData(null); setError(null); setNotice(null); setCastPage(1); setRosterPage(1); setRosterSearch(''); setRoom('studio'); setDesigns([]);
    if (!uuid) return;
    setLoading(true);
    try {
      const resp = await apiClient.get(`/litgroups/${uuid}/casting`);
      setData(resp.data);
      fetchDesigns(uuid);
      fetchEstimate(uuid);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load casting data');
    } finally { setLoading(false); }
  };

  // Refetch just the casting data (preserves page/filters) after an attach/detach.
  const refreshCasting = async () => {
    if (!litGroupUuid) return;
    try {
      const resp = await apiClient.get(`/litgroups/${litGroupUuid}/casting`);
      setData(resp.data);
    } catch (e) { setError(e.response?.data?.error || e.message || 'refresh failed'); }
  };

  // Close the derivation SSE stream on unmount.
  useEffect(() => () => { if (esRef.current) { esRef.current.close(); esRef.current = null; } }, []);

  // Run a casting job (full derive / incremental update / auto-cast) — background, SSE progress.
  // `path` is the route suffix ('casting/derive' | 'casting/update' | 'casting/auto-cast');
  // `body` carries e.g. { targetUuids } for a pick-and-choose auto-cast (empty ⇒ server default).
  const runCastingJob = async (path, body = {}) => {
    if (!litGroupUuid) return;
    setError(null); setNotice(null);
    setDeriving(true); setDeriveProgress({ percent: 3, message: 'Starting…' });
    try {
      if (esRef.current) { esRef.current.close(); esRef.current = null; }
      if (userId) {
        const es = new EventSource(`${config.BASE_URL}/sse-endpoint?userId=${userId}`);
        esRef.current = es;
        es.onmessage = (event) => {
          try {
            const parsed = JSON.parse(event.data);
            const d = parsed.data || parsed;
            if (d.litGroupUuid && d.litGroupUuid !== litGroupUuid) return;
            if (d.type === 'CASTING_PROGRESS') {
              setDeriveProgress({ percent: d.percent ?? 0, message: d.message || d.step || 'Working…' });
            } else if (d.type === 'CASTING_COMPLETE') {
              es.close(); esRef.current = null;
              setDeriving(false); setDeriveProgress(null);
              // A quota refusal is ElevenLabs' to explain, not ours to guess at: the account
              // has three separate ceilings (voice slots, monthly add/edit operations, TTS
              // characters) and this notice used to blame the slot cap for all of them.
              if (d.capMessage) setError(`ElevenLabs stopped the casting run: ${d.capMessage}`);
              setNotice(
                d.autoCast
                  // `reused` voices spent none of the 1040 monthly add/edit operations — a
                  // ceiling identical on Business and Scale, so reuse is the only lever.
                  ? `Auto-cast ${d.attached} voice${d.attached === 1 ? '' : 's'}${d.reused ? `, ${d.reused} reused from earlier casting (no ElevenLabs quota used)` : ''}${d.stoppedAtCap ? (d.capMessage ? ' (stopped by ElevenLabs — see the error above)' : ' (stopped early)') : ''} — review & re-roll in the cast table.`
                  : d.updated !== undefined
                    ? (d.updated > 0 ? `Updated casting — ${d.updated} character${d.updated === 1 ? '' : 's'} newly cast.` : 'Casting is up to date — everyone was already cast.')
                    : `Casting derived — ${d.styleCount} voice styles across ${d.characterCount} characters.`);
              load(litGroupUuid);
            } else if (d.type === 'CASTING_ERROR') {
              es.close(); esRef.current = null;
              setDeriving(false); setDeriveProgress(null);
              setError(d.message || 'Casting job failed');
            }
          } catch (_) { /* ignore non-JSON keepalives */ }
        };
        es.onerror = () => { /* non-fatal — progress just won't stream live */ };
      }
      await apiClient.post(`/litgroups/${litGroupUuid}/${path}`, body);
    } catch (e) {
      if (esRef.current) { esRef.current.close(); esRef.current = null; }
      setDeriving(false); setDeriveProgress(null);
      setError(e.response?.data?.error || e.message || 'Failed to start the casting job');
    }
  };

  const deriveCasting = () => runCastingJob('casting/derive');
  const updateCasting = () => runCastingJob('casting/update');
  const rederiveCasting = () => {
    if (window.confirm('Re-derive the casting for this Literary Group?\n\nRebuilds the voice-style roster and every character’s suggested style. Your attached designed voices are preserved.')) {
      runCastingJob('casting/derive');
    }
  };

  // Auto-cast: bulk-generate + attach a designed voice per lead (Voice-Design first pass).
  const autoCastLeads = () => {
    const n = (data?.characters || []).filter((c) => c.tier === 'lead' && !c.designUuid).length;
    if (n === 0) return;
    if (window.confirm(
      `Auto-generate + attach designed voices for ${n} lead${n === 1 ? '' : 's'}?\n\n` +
      `The casting director drafts a distinct voice per lead from its dialogue, generates it, and attaches ` +
      `it — spending ~${n} ElevenLabs voice slot${n === 1 ? '' : 's'} + ~${n} operation${n === 1 ? '' : 's'}. ` +
      `You can re-roll or detach any of them afterward.`)) {
      runCastingJob('casting/auto-cast');
    }
  };

  // ── Pick-and-choose auto-cast panel (modeled on the Render Coverage Report) ──
  // A character is auto-castable unless it has a REAL VA recording (hasVoice but
  // no designUuid). `designUuid` set ⇒ designed voice (re-rollable); neither ⇒ unvoiced.
  const isRealRecorded = (c) => !!c.hasVoice && !c.designUuid;
  const castable = (data?.characters || []).filter((c) => c.uuid && !isRealRecorded(c));
  const castOrdered = [...castable].sort((a, b) =>
    // unvoiced first, then by tier (lead→support→walkon), then by line count desc
    (a.hasVoice === b.hasVoice ? 0 : a.hasVoice ? 1 : -1) ||
    (TIER_RANK[a.tier] ?? 9) - (TIER_RANK[b.tier] ?? 9) ||
    (b.lines || 0) - (a.lines || 0)
  );
  const unvoicedUuids = castOrdered.filter((c) => !c.hasVoice).map((c) => c.uuid);
  const toggleCastSel = (uuid) =>
    setCastSel((prev) => { const n = new Set(prev); n.has(uuid) ? n.delete(uuid) : n.add(uuid); return n; });
  const selectCast = (uuids) => setCastSel(new Set(uuids));

  const autoCastSelected = () => {
    const ids = [...castSel].filter((u) => castable.some((c) => c.uuid === u));
    if (ids.length === 0) return;
    const reVoicing = ids.filter((u) => castable.find((c) => c.uuid === u)?.designUuid).length;
    if (window.confirm(
      `Generate + attach designed voices for ${ids.length} selected character${ids.length === 1 ? '' : 's'}?\n\n` +
      (reVoicing ? `${reVoicing} already have a designed voice and will be RE-VOICED (the old one is replaced).\n\n` : '') +
      `Spends ~${ids.length} ElevenLabs voice slot${ids.length === 1 ? '' : 's'} + ~${ids.length} operation${ids.length === 1 ? '' : 's'}. ` +
      `You can re-roll or detach any of them afterward.`)) {
      setCastSel(new Set());
      runCastingJob('casting/auto-cast', { targetUuids: ids });
    }
  };

  // Attach a designed voice to one character (Casting Studio audition/preview + the render).
  const attachDesign = async (c, voiceDesignUuid, force = false) => {
    if (!voiceDesignUuid) return;
    setDesignBusy(c.uuid); setError(null);
    try {
      await apiClient.post(`/characters/${c.uuid}/voice-design`, { voiceDesignUuid, force });
      await refreshCasting();
    } catch (e) {
      const resp = e.response?.data;
      if (resp?.code === 'has-recording' && !force) {
        if (window.confirm(`${c.name} already has a recorded voice. Override it with the designed voice?`)) {
          setDesignBusy(null);
          return attachDesign(c, voiceDesignUuid, true);
        }
      } else {
        setError(`${c.name}: ${resp?.error || e.message || 'attach failed'}`);
      }
    } finally { setDesignBusy(null); }
  };

  const detachDesign = async (c) => {
    setDesignBusy(c.uuid); setError(null);
    try {
      await apiClient.delete(`/characters/${c.uuid}/voice-design`);
      await refreshCasting();
    } catch (e) {
      setError(`${c.name}: ${e.response?.data?.error || e.message || 'detach failed'}`);
    } finally { setDesignBusy(null); }
  };

  // Bulk: apply / clear a designed voice across every character currently in a style.
  const bulkAttachStyle = async (styleKey, styleName, voiceDesignUuid) => {
    if (!voiceDesignUuid) return;
    setDesignBusy(`style:${styleKey}`); setError(null); setNotice(null);
    try {
      const resp = await apiClient.post(`/litgroups/${litGroupUuid}/voice-styles/${encodeURIComponent(styleKey)}/voice-design`, { voiceDesignUuid });
      const { attached, skipped } = resp.data || {};
      await refreshCasting();
      const dName = (designs.find(d => d.uuid === voiceDesignUuid) || {}).name || 'the designed voice';
      setNotice(`Applied “${dName}” to ${attached} character${attached === 1 ? '' : 's'} in “${styleName}”.` +
        (skipped ? ` Skipped ${skipped} already-recorded.` : ''));
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'bulk attach failed');
    } finally { setDesignBusy(null); }
  };

  const bulkDetachStyle = async (styleKey, styleName, count) => {
    if (!window.confirm(
      `Remove the designed voice from all ${count} character${count === 1 ? '' : 's'} in “${styleName}”?\n\n` +
      `Real voice-actor recordings are not affected.`)) return;
    setDesignBusy(`style:${styleKey}`); setError(null); setNotice(null);
    try {
      await apiClient.delete(`/litgroups/${litGroupUuid}/voice-styles/${encodeURIComponent(styleKey)}/voice-design`);
      await refreshCasting();
      setNotice(`Removed the designed voice from “${styleName}”.`);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'bulk clear failed');
    } finally { setDesignBusy(null); }
  };

  const pct = (a, b) => (b > 0 ? Math.round((100 * a) / b) : 0);

  const selectedName = (options.find(o => o.value === litGroupUuid) || {}).text || '';

  // Options for the per-row style selector (the roster, scrollable/searchable).
  const styleOptions = (data?.styles || []).map(s => ({
    key: s.canonicalKey, value: s.canonicalKey, text: `${s.name} · ${s.gender}/${s.age}`,
  }));

  // Saved Voice Designs, as attach options.
  const designOptions = (designs || []).map(d => ({ key: d.uuid, value: d.uuid, text: d.name }));

  // Leads that don't yet have an attached designed voice — the Auto-cast target.
  const autoCastLeadCount = (data?.characters || []).filter(c => c.tier === 'lead' && !c.designUuid).length;

  // Filter options.
  const tierOptions = [
    { key: 'all', value: '', text: 'All tiers' },
    { key: 'narration', value: 'narration', text: 'Narrator' },
    { key: 'lead', value: 'lead', text: 'Lead' },
    { key: 'support', value: 'support', text: 'Support' },
    { key: 'walkon', value: 'walkon', text: 'Walk-on' },
    { key: 'nonspeaking', value: 'nonspeaking', text: 'Non-speaking' },
  ];
  const archetypeOptions = [{ key: 'all', value: '', text: 'All archetypes' }].concat(
    [...new Set((data?.characters || []).map(c => c.archetype).filter(Boolean))].sort()
      .map(a => ({ key: a, value: a, text: a }))
  );

  // Filter the cast, then page the filtered result.
  const filteredCast = (data?.characters || []).filter(c => {
    /**
     * ⚠️ SPEAKS IN, not "belongs to". `bookLines` is counted from the lines a character actually
     * speaks, so a roster entry with no dialogue in the chosen book is correctly hidden — which is
     * the question an ingestion raises. A character with NO bookLines at all (nothing to say
     * anywhere) therefore disappears under any single-book filter and is visible under All books.
     */
    if (castBookFilter !== CAST_ALL_BOOKS
      && !(c.bookLines || []).some(b => b.bookId === castBookFilter)) return false;
    if (tierFilter && c.tier !== tierFilter) return false;
    if (archetypeFilter && c.archetype !== archetypeFilter) return false;
    if (castSearch) {
      const hay = `${c.name} ${c.archetype || ''} ${c.style || ''}`.toLowerCase();
      if (!hay.includes(castSearch.toLowerCase())) return false;
    }
    return true;
  });
  // ── Pooled voice clone ────────────────────────────────────────────────────────────────────
  //
  // One voice per character, built from a selected set of its approved recordings, instead of one
  // voice per emotion. Both cost TWO of the account's 1040 monthly add/edit operations — a ceiling
  // that is the same on Business as on Scale and cannot be bought — so pooling a 13-emotion
  // character is 2 operations rather than 26.
  //
  // ⭐ ALWAYS DRY RUN FIRST, and the UI enforces it: the button previews, and only the modal can
  // mint. The selection is a duration budget against ElevenLabs' 1-2 minute ideal, so which
  // emotions make the cut is a real decision — and once the operations are spent they are gone.
  const [poolBusy, setPoolBusy] = useState(null);      // character uuid being previewed/minted
  const [poolPreview, setPoolPreview] = useState(null); // dry-run result, drives the modal
  const [poolError, setPoolError] = useState(null);
  const [poolMinting, setPoolMinting] = useState(false);
  const [poolResult, setPoolResult] = useState(null);

  const previewPool = async (c) => {
    setPoolBusy(c.uuid); setPoolError(null); setPoolResult(null);
    try {
      const r = await apiClient.post('/voice-clones/pooled', {
        characterUuid: c.uuid, dryRun: true
      });
      setPoolPreview({ ...r.data, character: c });
    } catch (e) {
      // 422 is the expected answer for "not poolable yet" — nothing recorded, nothing normalized,
      // or nothing spoken. It is information, not a failure, so it reads as a message rather than
      // an error banner.
      const msg = e?.response?.data?.message || e.message;
      setPoolError({ character: c, message: msg, notPoolable: e?.response?.status === 422 });
    } finally {
      setPoolBusy(null);
    }
  };

  const mintPool = async () => {
    if (!poolPreview?.character) return;
    setPoolMinting(true);
    try {
      const r = await apiClient.post('/voice-clones/pooled', {
        characterUuid: poolPreview.character.uuid
      });
      setPoolResult(r.data);
      setPoolPreview(null);
      if (litGroupUuid) load(litGroupUuid);
    } catch (e) {
      setPoolError({
        character: poolPreview.character,
        message: e?.response?.data?.message || e.message
      });
      setPoolPreview(null);
    } finally {
      setPoolMinting(false);
    }
  };

  const totalCount = data ? data.characters.length : 0;
  const castTotal = filteredCast.length;
  const totalCastPages = Math.max(1, Math.ceil(castTotal / CAST_PAGE_SIZE));
  const castStart = (castPage - 1) * CAST_PAGE_SIZE;
  const pagedCast = filteredCast.slice(castStart, castStart + CAST_PAGE_SIZE);
  const castFiltered = !!(castSearch || tierFilter || archetypeFilter
    || castBookFilter !== CAST_ALL_BOOKS);

  // Roster search + paging (no scroll box — the box clipped the per-row dropdown menus).
  const allStyles = data ? data.styles : [];
  const rq = rosterSearch.trim().toLowerCase();
  const filteredStyles = rq
    ? allStyles.filter(s => `${s.name} ${s.gender} ${s.age} ${s.elevenVoiceName || ''} ${s.appliedDesign || ''}`.toLowerCase().includes(rq))
    : allStyles;
  const rosterFiltered = !!rq;
  const rosterTotal = filteredStyles.length;
  const totalRosterPages = Math.max(1, Math.ceil(rosterTotal / ROSTER_PAGE_SIZE));
  const rosterStart = (Math.min(rosterPage, totalRosterPages) - 1) * ROSTER_PAGE_SIZE;
  const pagedStyles = filteredStyles.slice(rosterStart, rosterStart + ROSTER_PAGE_SIZE);

  return (
    <Container fluid className="landing-page">
      <Container style={{ marginTop: '30px', marginBottom: '2em' }}>
        <Header as="h1">
          <Icon name="users" />
          <Header.Content>
            AAI Talent Agency
            <Header.Subheader>Casting Studio — tier your cast and see the voice-style roster</Header.Subheader>
          </Header.Content>
        </Header>

        {/* Deliberately NOT sticky — it scrolls away with everything else.
          *
          * It was pinned for a while, on the reasoning that every panel below is scoped to this
          * selection so the selection should stay visible. Two things beat that reasoning in
          * practice. A sticky element creates its own stacking context, and the page is full of
          * Semantic dropdowns and popovers whose menus then paint OVER the pinned form — winnable
          * with enough z-index archaeology, but not worth it. And the scroll back up costs nothing
          * real: changing group is a rare act, and the group you switch to re-renders immediately
          * below this form, so you land looking straight at the thing you just chose. */}
        <Segment>
          <span style={{ fontWeight: 600, marginRight: '1em' }}>Literary Group</span>
          <Dropdown
            placeholder="Select a Literary Group"
            selection
            search
            clearable
            options={options}
            value={litGroupUuid || ''}
            onChange={(e, { value }) => load(value || null)}
            style={{ minWidth: 320 }}
          />
          {options.length === 0 && (
            <span style={{ marginLeft: '1em', color: '#999' }}>No published Literary Groups yet.</span>
          )}
        </Segment>

        {/* Agency "rooms" — plain nav for now; illustrate the building later. */}
        {litGroupUuid && (
          <Menu pointing secondary style={{ marginTop: '1em' }}>
            <Menu.Item name="Casting Studio" active={room === 'studio'} onClick={() => setRoom('studio')} />
            <Menu.Item name="Voice Design Studio" active={room === 'voicedesign'} onClick={() => setRoom('voicedesign')} />
            <Menu.Item name="Green Room · Curate" active={room === 'greenroom'} onClick={() => setRoom('greenroom')} />
            <Menu.Item name="Ingest Lab" active={room === 'ingest'} onClick={() => setRoom('ingest')} />
          </Menu>
        )}

        {/* Ingest Lab — layout-aware ingest preview (read-only BC confirmation gate). */}
        {room === 'ingest' && litGroupUuid && (
          <IngestPreviewPanel litGroupUuid={litGroupUuid} books={(data && data.books) || []}
            engineVersion={(data && data.ingestEngineVersion) || null} />
        )}

        {/* Voice Design Studio — design bespoke voices from a prompt. */}
        {room === 'voicedesign' && litGroupUuid && (
          <VoiceDesignStudio litGroupUuid={litGroupUuid} onDesignsChanged={() => fetchDesigns()} />
        )}

        {/* Green Room — curation (the human "who's in it" work), reusing Manage Cast. */}
        {room === 'greenroom' && litGroupUuid && (
          <Segment>
            <Header as="h3">
              <Icon name="users" />
              <Header.Content>
                Green Room — Curate the Cast
                <Header.Subheader>Merge duplicate names and dismiss spurious characters before casting</Header.Subheader>
              </Header.Content>
            </Header>
            <p style={{ color: '#666' }}>Clean the cast first — a tidy cast makes for a tighter voice roster.</p>
            {/*
              ⭐ A cast voice has to reach THIS page's dropdown. The design list is fetched once
              per LitGroup selection, so without this signal the voice exists, is attached, and
              is still missing from the control next to the character.
            */}
            <ManageCastModal
              inline
              litGroupUuid={litGroupUuid}
              litGroupName={selectedName}
              /*
                ⛔ NOT `load()`. That is the SELECT-A-LITGROUP routine: it resets the room to
                ‘studio’, clears the cast data, the pages and the search. Using it as a refresh
                threw the operator out of the Green Room and back to the Casting Studio on every
                cast — the Auditions cards they were working through vanished mid-pass.

                ⭐ `refreshCasting` is the one that exists for exactly this, and says so in its
                own comment: refetch the casting data and keep page, filters and room.
              */
              onVoiceCast={() => { fetchDesigns(litGroupUuid); refreshCasting(); }}
            />
          </Segment>
        )}

        {room === 'studio' && (
          <>
            {loading && <Segment><Loader active inline size="small" /> <span style={{ marginLeft: '0.5em' }}>Loading casting…</span></Segment>}
            {error && <Message negative onDismiss={() => setError(null)}><Icon name="warning sign" />{error}</Message>}
            {notice && <Message positive onDismiss={() => setNotice(null)}><Icon name="check circle" />{notice}</Message>}

            {data && !loading && deriving && (
              <Segment textAlign="center" style={{ padding: '2.5em 1em' }}>
                <Loader active inline size="large" />
                <Header as="h4" style={{ marginTop: '1em' }}>Deriving casting…</Header>
                <p style={{ color: '#666' }}>{deriveProgress?.message || 'Working…'}</p>
                <div style={{ maxWidth: 420, margin: '0.5em auto 0' }}>
                  <div style={{ background: '#eee', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                    <div style={{ width: `${deriveProgress?.percent || 0}%`, background: '#2185d0', height: '100%', transition: 'width .3s' }} />
                  </div>
                </div>
                <p style={{ color: '#999', fontSize: '0.85em', marginTop: '0.8em' }}>
                  AI labeling + clustering + audition scripts — spends OpenAI credits, a few minutes for a large cast.
                </p>
              </Segment>
            )}

            {data && !loading && !deriving && data.styleCount === 0 && (
              <Segment placeholder textAlign="center">
                <Header icon>
                  <Icon name="magic" />
                  No casting derived yet
                </Header>
                <p style={{ color: '#666', maxWidth: 540, margin: '0 auto 1em' }}>
                  Derive the casting to build this Literary Group’s voice-style roster — tier the cast,
                  infer a reusable voice style per character, and cluster them into a roster with original
                  audition scripts. Voices are added afterward in the Voice Design Studio.<br />
                  <strong>Tip:</strong> curate the cast in the Green Room first (merge duplicates, dismiss
                  phantoms) for a tighter roster.
                </p>
                <Button primary size="large" onClick={deriveCasting}>
                  <Icon name="magic" /> Derive casting
                </Button>
                <p style={{ color: '#999', fontSize: '0.85em', marginTop: '0.8em' }}>Spends OpenAI credits; a few minutes for a large cast.</p>
              </Segment>
            )}

            {data && !loading && !deriving && data.styleCount > 0 && (
              <>
                {/* Tier funnel */}
                <Segment>
                  <Statistic.Group widths="five" size="small">
                    <Statistic color="violet"><Statistic.Value>{data.tiers.narration}</Statistic.Value><Statistic.Label>Narrator</Statistic.Label></Statistic>
                    <Statistic color="green"><Statistic.Value>{data.tiers.lead}</Statistic.Value><Statistic.Label>Leads · human VA</Statistic.Label></Statistic>
                    <Statistic color="blue"><Statistic.Value>{data.tiers.support}</Statistic.Value><Statistic.Label>Support · Agency</Statistic.Label></Statistic>
                    <Statistic color="grey"><Statistic.Value>{data.tiers.walkon}</Statistic.Value><Statistic.Label>Walk-on · Agency</Statistic.Label></Statistic>
                    <Statistic><Statistic.Value>{data.styleCount}</Statistic.Value><Statistic.Label>Voice styles</Statistic.Label></Statistic>
                  </Statistic.Group>
                  {data.totalLines > 0 && (
                    <p style={{ marginTop: '1em', color: '#666' }}>
                      {/* "The Agency covers the rest" was true only while narration was invisible.
                        * Once narration counts, the narrator is typically the LARGEST single share
                        * of the dialogue — 41% against the leads' 36% in the canary — and it is not
                        * agency work: it is one voice, cast once, often before anything else and
                        * sometimes by the Book Creator themselves. Naming it separately keeps the
                        * sentence honest about what the Agency is actually being asked to absorb. */}
                      <Icon name="info circle" />{' '}
                      {data.tiers.narration > 0 && (
                        <>
                          The narrator carries <strong>{pct(data.tierLines.narration || 0, data.totalLines)}%</strong> of the
                          dialogue on {data.tiers.narration === 1 ? 'one voice' : `${data.tiers.narration} voices`}.{' '}
                        </>
                      )}
                      {data.tiers.lead} leads carry <strong>{pct(data.leadLines, data.totalLines)}%</strong>{' '}
                      ({data.leadLines.toLocaleString()}/{data.totalLines.toLocaleString()} lines). The Agency covers
                      support and walk-ons{data.tiers.nonspeaking ? ` · ${data.tiers.nonspeaking} non-speaking (merge candidates)` : ''}.
                    </p>
                  )}

                  {/* ⭐ WHO CARRIES THE OTHER 65%. The sentence above says what the leads hold and
                      nothing about the rest, so a cast of 55 walk-ons holding four lines each reads
                      exactly like 55 phantoms holding none — and those call for opposite actions.
                      A merge pass is worth doing against duplicates; it does nothing against a
                      genuinely large crowd. */}
                  {data.tierLines && data.totalLines > 0 && (
                    <Table basic="very" compact="very" size="small" style={{ marginTop: '0.4em' }}>
                      <Table.Body>
                        {[
                          ['narration', 'Narrator', 'violet'],
                          ['lead', 'Leads', 'green'],
                          ['support', 'Support', 'blue'],
                          ['walkon', 'Walk-on', 'grey'],
                          ['nonspeaking', 'Non-speaking', 'red'],
                        ].filter(([k]) => data.tiers[k] > 0).map(([k, label, color]) => (
                          <Table.Row key={k}>
                            <Table.Cell style={{ width: '9em' }}><Label size="mini" color={color} horizontal>{label}</Label></Table.Cell>
                            <Table.Cell style={{ width: '6em' }}>{data.tiers[k]} chars</Table.Cell>
                            <Table.Cell style={{ width: '9em' }}>
                              {(data.tierLines[k] || 0).toLocaleString()} lines
                            </Table.Cell>
                            <Table.Cell>
                              <strong>{pct(data.tierLines[k] || 0, data.totalLines)}%</strong> of the dialogue
                              {/* A mean is only worth printing when the tier is roughly uniform.
                                * The narration tier in the canary holds 3,524 lines and 2 lines,
                                * which averaged to "1763.0 lines each" — a number describing
                                * neither character and hiding the fact that the 2-line one is a
                                * merge candidate. Below four members the spread is shown instead. */}
                              {data.tiers[k] > 0 && (
                                <span style={{ color: '#999' }}>
                                  {data.tiers[k] >= 4
                                    ? `${' · '}${((data.tierLines[k] || 0) / data.tiers[k]).toFixed(1)} lines each`
                                    : `${' · '}across ${data.tiers[k]} ${data.tiers[k] === 1 ? 'role' : 'roles'}`}
                                </span>
                              )}
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  )}
                </Segment>

                {/* ⭐ WHAT CASTING WOULD COST — before anything is spent, and on the screen where the
                    decision is made rather than one navigation away. */}
                <Segment>
                  <Header as="h4" style={{ marginBottom: '0.6em' }}>
                    <Icon name="calculator" /> What casting this roster would cost
                  </Header>
                  {estimateErr && (
                    <Message size="tiny" warning>
                      <Icon name="warning sign" /> Estimate unavailable — {estimateErr}.
                      {' '}The cast screen will still show it before you commit.
                    </Message>
                  )}
                  {!estimate && !estimateErr && <p style={{ color: '#999' }}><Icon loading name="spinner" /> Reading the account…</p>}
                  {estimate && (
                    <>
                      <Statistic.Group widths="four" size="mini">
                        <Statistic><Statistic.Value>{estimate.targets}</Statistic.Value><Statistic.Label>Cast targets</Statistic.Label></Statistic>
                        <Statistic color={estimate.reusable > 0 ? 'green' : 'grey'}>
                          <Statistic.Value>{estimate.reusable}</Statistic.Value><Statistic.Label>Reuse an existing voice</Statistic.Label>
                        </Statistic>
                        <Statistic color="orange"><Statistic.Value>{estimate.willBuy}</Statistic.Value><Statistic.Label>Need a new voice</Statistic.Label></Statistic>
                        <Statistic color={estimate.fits === false ? 'red' : 'blue'}>
                          <Statistic.Value>{estimate.opsNeeded}</Statistic.Value><Statistic.Label>Ops needed</Statistic.Label>
                        </Statistic>
                      </Statistic.Group>

                      <p style={{ marginTop: '1em', color: '#666' }}>
                        {estimate.free ? (
                          <><Icon name="check circle" color="green" /> <strong>Costs nothing</strong> — every target already has a live voice to reuse.</>
                        ) : (
                          <>
                            <Icon name="info circle" /> {estimate.willBuy} voice{estimate.willBuy === 1 ? '' : 's'} × {estimate.opsPerVoice} operations
                            {' = '}<strong>{estimate.opsNeeded} of your monthly add/edit budget</strong>
                            {estimate.opsRemaining != null
                              ? <> · {estimate.opsRemaining.toLocaleString()} remaining{estimate.opsMax ? ` of ${estimate.opsMax.toLocaleString()}` : ''}</>
                              : <span style={{ color: '#999' }}> · account budget unreadable</span>}
                          </>
                        )}
                      </p>

                      {/* ⚠️ The failure that looks like a result. If listVoices() cannot be read the
                          service disables reuse for the run and falls back to "has no voice at all",
                          which INFLATES the estimate for a network reason rather than a casting one. */}
                      {estimate.targets > 0 && estimate.reusable === 0 && (
                        <Message size="tiny" warning>
                          <Icon name="warning sign" /> <strong>No target reused an existing voice.</strong> Expected on a
                          first-ever cast. On a re-import it means the name match missed — or the account voice list could not
                          be read, which disables reuse silently and inflates this number. Worth checking before you commit.
                        </Message>
                      )}
                      {estimate.fits === false && (
                        <Message size="tiny" negative>
                          <Icon name="dont" /> <strong>Over budget by {estimate.shortfall} operations.</strong> Add/edit
                          operations do not scale with plan and cannot be bought — cast a subset, or wait for the reset.
                        </Message>
                      )}
                      {/*
                        ⭐ WHO, NOT HOW MANY. The operator asked "which characters does 'Auto-cast leads
                        (1)' represent?" — and four statistics could not answer it. A count is not a
                        decision; a name is.

                        ⛔ AND THE BUTTON'S COUNT CAN DISAGREE WITH THE RUN. It is computed locally as
                        `tier === 'lead' && !designUuid`, while the server selects on
                        `castingTier = 'lead' OR name = 'narrator'`, additionally requires a
                        suggestedArchetype, excludes anything already holding a LIVE voice, excludes
                        anything with a real HUMAN clone, and caps the run. This list comes from the
                        server's own selection, so what is shown is what would happen.
                      */}
                      {Array.isArray(estimate.plan) && estimate.plan.length > 0 && (
                        <Table compact="very" size="small" basic="very" style={{ marginTop: '0.5em' }}>
                          <Table.Header>
                            <Table.Row>
                              <Table.HeaderCell>Character</Table.HeaderCell>
                              <Table.HeaderCell textAlign="right">Lines</Table.HeaderCell>
                              <Table.HeaderCell>What this run would do</Table.HeaderCell>
                            </Table.Row>
                          </Table.Header>
                          <Table.Body>
                            {estimate.plan.map((p) => (
                              <Table.Row key={p.uuid} warning={p.action === 'buy'}>
                                <Table.Cell>
                                  <strong>{p.name}</strong>
                                  {p.gender && p.gender !== 'unknown' && (
                                    <span style={{ color: '#999' }}> · {p.gender}</span>
                                  )}
                                </Table.Cell>
                                <Table.Cell textAlign="right">{p.lineCount}</Table.Cell>
                                <Table.Cell>
                                  {p.action === 'reuse' ? (
                                    <span style={{ color: '#21ba45' }}>
                                      <Icon name="recycle" /> reuse{p.reusingVoice ? ` ${p.reusingVoice}` : ''} — free
                                    </span>
                                  ) : (
                                    <span style={{ color: '#f2711c' }}>
                                      <Icon name="magic" /> design a new voice — {estimate.opsPerVoice} ops
                                    </span>
                                  )}
                                </Table.Cell>
                              </Table.Row>
                            ))}
                          </Table.Body>
                        </Table>
                      )}
                      {estimate.cappedAt && (
                        <p style={{ color: '#999', fontSize: '0.9em' }}>
                          Showing the first {estimate.cappedAt} of {estimate.totalBeforeCap} eligible characters — one run's cap.
                        </p>
                      )}
                    </>
                  )}
                </Segment>

                {/* Voice-style roster — no scroll box (it clipped the per-row dropdown menus). */}
                <Header as="h3" dividing>
                  {autoCastLeadCount > 0 && (
                    <Button size="mini" color="purple" floated="right" onClick={autoCastLeads}
                      title={`Have the casting director generate + attach a distinct designed voice for each of your ${autoCastLeadCount} un-voiced lead${autoCastLeadCount === 1 ? '' : 's'} (spends ElevenLabs slots/ops)`}>
                      <Icon name="magic" /> Auto-cast leads ({autoCastLeadCount})
                    </Button>
                  )}
                  <Button size="mini" basic={!castPanelOpen} color={castPanelOpen ? 'purple' : undefined} floated="right"
                    onClick={() => setCastPanelOpen((o) => !o)}
                    title="Voice or re-voice a hand-picked set of characters (bulk Voice-Design)">
                    <Icon name={castPanelOpen ? 'caret down' : 'magic'} /> Voice / re-voice cast…
                  </Button>
                  {data.uncastCount > 0 && (
                    <Button size="mini" color="teal" floated="right" onClick={updateCasting}
                      title={`${data.uncastCount} character${data.uncastCount === 1 ? '' : 's'} have no suggested style yet — cast them into the existing roster (cheap; no re-cluster)`}>
                      <Icon name="add user" /> Update casting ({data.uncastCount})
                    </Button>
                  )}
                  <Button size="mini" basic floated="right" onClick={rederiveCasting}
                    title="Rebuild the whole roster from the current (curated) cast — designed voices are preserved">
                    <Icon name="refresh" /> Re-derive
                  </Button>
                  <Icon name="microphone" /> Voice-Style Roster ({rosterFiltered ? `${rosterTotal} of ${allStyles.length}` : allStyles.length})
                  {rosterTotal > ROSTER_PAGE_SIZE && (
                    <span style={{ fontSize: '0.7em', fontWeight: 300, color: '#888', marginLeft: '0.6em' }}>
                      showing {rosterStart + 1}–{Math.min(rosterStart + ROSTER_PAGE_SIZE, rosterTotal)}
                    </span>
                  )}
                </Header>

                {/* Pick-and-choose auto-cast — modeled on the Render Coverage Report, litGroup-scoped. */}
                {castPanelOpen && (
                  <Segment className="ta-autocast-panel" style={{ marginBottom: '0.9em' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <span style={{ fontWeight: 600 }}><Icon name="magic" /> Voice / re-voice characters</span>
                      <Button icon basic size="mini" onClick={() => setCastPanelOpen(false)} title="Close">
                        <Icon name="close" />
                      </Button>
                    </div>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>
                      Pick characters, then the casting director generates + attaches a distinct designed voice for each
                      (~1 ElevenLabs slot + op per pick). Re-selecting a <Label size="mini" color="purple" basic>designed</Label>
                      character re-rolls it. Characters with a real recorded (VA) voice are not shown.
                    </div>
                    <Button.Group size="mini" style={{ marginBottom: 6 }}>
                      <Button basic onClick={() => selectCast(unvoicedUuids)} disabled={unvoicedUuids.length === 0}>
                        All unvoiced ({unvoicedUuids.length})
                      </Button>
                      <Button basic onClick={() => selectCast(castOrdered.map((c) => c.uuid))} disabled={castOrdered.length === 0}>
                        All ({castOrdered.length})
                      </Button>
                      <Button basic onClick={() => setCastSel(new Set())} disabled={castSel.size === 0}>
                        Clear
                      </Button>
                    </Button.Group>
                    {castOrdered.length === 0 ? (
                      <div style={{ fontSize: 12, color: '#999' }}>No auto-castable characters (derive the cast first).</div>
                    ) : (
                      <List divided size="small" style={{ maxHeight: 260, overflowY: 'auto', margin: 0 }}>
                        {castOrdered.map((c) => (
                          <List.Item key={c.uuid} style={{ padding: '5px 2px' }}>
                            <List.Content floated="right">
                              {c.designUuid
                                ? <Label size="mini" color="purple" basic><Icon name="cogs" /> designed</Label>
                                : c.hasVoice
                                  ? <Label size="mini" color="green" basic><Icon name="microphone" /> voiced</Label>
                                  : <Label size="mini" color="orange"><Icon name="warning sign" /> no voice</Label>}
                            </List.Content>
                            <Checkbox
                              checked={castSel.has(c.uuid)}
                              onChange={() => toggleCastSel(c.uuid)}
                              label={
                                <label style={{ fontSize: 13 }}>
                                  {c.name}
                                  <span style={{ color: '#999', fontSize: 11 }}>
                                    {' · '}{c.tier || 'cast'}{typeof c.lines === 'number' ? ` · ${c.lines} line${c.lines === 1 ? '' : 's'}` : ''}
                                  </span>
                                </label>
                              }
                            />
                          </List.Item>
                        ))}
                      </List>
                    )}
                    <div style={{ marginTop: 8 }}>
                      <Button size="tiny" color="purple" disabled={castSel.size === 0} onClick={autoCastSelected}>
                        <Icon name="magic" /> Voice / re-voice {castSel.size || ''} selected
                      </Button>
                    </div>
                  </Segment>
                )}
                <div style={{ display: 'flex', gap: '0.6em', alignItems: 'center', marginBottom: '0.7em' }}>
                  <Input
                    icon="search" iconPosition="left" size="small"
                    placeholder="Search style / gender / voice / applied design…"
                    value={rosterSearch}
                    onChange={(e, { value }) => setRosterSearch(value)}
                    style={{ minWidth: 300 }}
                  />
                  {rosterFiltered && (
                    <Button size="mini" basic onClick={() => setRosterSearch('')}>
                      <Icon name="close" /> Clear
                    </Button>
                  )}
                </div>
                <div>
                  <Table compact celled className="ta-table">
                    <Table.Header><Table.Row>
                      <Table.HeaderCell>Style</Table.HeaderCell>
                      <Table.HeaderCell>Gender/Age</Table.HeaderCell>
                      <Table.HeaderCell>Roles</Table.HeaderCell>
                      <Table.HeaderCell>ElevenLabs voice</Table.HeaderCell>
                      <Table.HeaderCell>Cloning sample</Table.HeaderCell>
                      <Table.HeaderCell>Apply a designed voice</Table.HeaderCell>
                    </Table.Row></Table.Header>
                    <Table.Body>
                      {pagedStyles.map((s, i) => (
                        <Table.Row key={i}>
                          <Table.Cell><strong>{s.name}</strong></Table.Cell>
                          <Table.Cell>{s.gender}/{s.age}</Table.Cell>
                          <Table.Cell>{s.demand}</Table.Cell>
                          <Table.Cell>
                            {s.elevenVoiceName
                              ? <span><Icon name="check circle" color="green" />{s.elevenVoiceName}</span>
                              : <Label size="mini" color="yellow">unmatched</Label>}
                          </Table.Cell>
                          <Table.Cell>
                            {s.sampleWords
                              ? <span><Icon name="file text" color="grey" />{s.sampleWords} words</span>
                              : <Label size="mini" color="yellow">none</Label>}
                          </Table.Cell>
                          <Table.Cell>
                            {designOptions.length ? (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5em', whiteSpace: 'nowrap' }}>
                                <Dropdown selection options={designOptions}
                                  value="" placeholder={s.appliedDesign ? 'change…' : `apply to all ${s.demand}…`} selectOnBlur={false}
                                  disabled={designBusy === `style:${s.canonicalKey}`}
                                  onChange={(e, { value }) => value && bulkAttachStyle(s.canonicalKey, s.name, value)}
                                  style={{ width: 190 }} />
                                {s.appliedDesign && (
                                  <Icon name="cogs" color="purple" style={{ cursor: 'help', margin: 0 }}
                                    title={`Applied to all castable ${s.name}: ${s.appliedDesign}`} />
                                )}
                                {s.appliedDesign && (
                                  <Button icon compact basic size="mini" color="red"
                                    title="Remove this designed voice from every character in this style"
                                    loading={designBusy === `style:${s.canonicalKey}`}
                                    onClick={() => bulkDetachStyle(s.canonicalKey, s.name, s.demand)}>
                                    <Icon name="trash alternate outline" />
                                  </Button>
                                )}
                              </span>
                            ) : <span style={{ color: '#bbb', fontSize: '0.85em' }}>design one first</span>}
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                </div>
                {rosterTotal === 0 && (
                  <Message info size="small" style={{ marginTop: '0.5em' }}>
                    <Icon name="search" /> No styles match “{rosterSearch}”.
                  </Message>
                )}
                {totalRosterPages > 1 && (
                  <div style={{ textAlign: 'center', marginTop: '0.6em' }}>
                    <Pagination
                      size="mini"
                      activePage={Math.min(rosterPage, totalRosterPages)}
                      totalPages={totalRosterPages}
                      boundaryRange={1}
                      siblingRange={1}
                      ellipsisItem={null}
                      onPageChange={(e, { activePage }) => setRosterPage(activePage)}
                    />
                  </div>
                )}

                {/* Cast — no overflow scroll box here: it would clip the per-row style dropdown at the
                container edge (overflow on one axis forces the other to clip too; z-index can't
                escape it). The page scrolls instead. (Pagination is a later, responsive-pass job.) */}
                <Header as="h3" dividing style={{ marginTop: '1.5em' }}>
                  <Icon name="users" /> Cast ({castFiltered ? `${castTotal} of ${totalCount}` : totalCount})
                  {castTotal > CAST_PAGE_SIZE && (
                    <span style={{ fontSize: '0.7em', fontWeight: 300, color: '#888', marginLeft: '0.6em' }}>
                      showing {castStart + 1}–{Math.min(castStart + CAST_PAGE_SIZE, castTotal)}
                    </span>
                  )}
                </Header>
                {/* `ta-filter-row` lifts an OPEN filter menu above the cast table's own gender and
                    archetype dropdowns — see talent-agency.css. */}
                <div className="ta-filter-row" style={{ display: 'flex', gap: '0.6em', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.7em' }}>
                  <Input
                    icon="search" iconPosition="left" size="small"
                    placeholder="Search name / archetype / style…"
                    value={castSearch}
                    onChange={(e, { value }) => setCastSearch(value)}
                    style={{ minWidth: 260 }}
                  />
                  {/*
                    ⭐ FIRST in the row, and labelled, because it changes what the list MEANS
                    rather than merely narrowing it — the same reasoning the Green Room's filter
                    carries. Shown only when there is more than one book, since with one book the
                    question does not arise.
                  */}
                  {(data?.books || []).length > 1 && (
                    <>
                      <strong style={{ whiteSpace: 'nowrap', fontSize: '0.92em' }}>Book Filter</strong>
                      <Dropdown
                        selection compact search
                        value={castBookFilter}
                        onChange={(e, { value }) => setCastBookFilter(value)}
                        style={{ minWidth: 240 }}
                        options={[
                          {
                            key: 'all', value: CAST_ALL_BOOKS,
                            text: `All books (${(data?.characters || []).length})`,
                          },
                          ...(data?.books || []).map(b => ({
                            key: b.uuid,
                            value: b.uuid,
                            // ⭐ The count is the reason to pick one, so it belongs in the option.
                            text: `${b.title} (${(data?.characters || [])
                              .filter(c => (c.bookLines || []).some(x => x.bookId === b.uuid)).length})`,
                          })),
                        ]}
                      />
                    </>
                  )}
                  <Dropdown selection compact options={tierOptions} value={tierFilter}
                    onChange={(e, { value }) => setTierFilter(value)} style={{ minWidth: 150 }} />
                  <Dropdown selection search scrolling clearable placeholder="All archetypes"
                    options={archetypeOptions} value={archetypeFilter}
                    onChange={(e, { value }) => setArchetypeFilter(value || '')} style={{ minWidth: 210 }} />
                  {castFiltered && (
                    <Button size="mini" basic onClick={() => { setCastSearch(''); setTierFilter(''); setArchetypeFilter(''); setCastBookFilter(CAST_ALL_BOOKS); }}>
                      <Icon name="close" /> Clear
                    </Button>
                  )}
                </div>
                <div>
                  <Table compact celled striped className="ta-table">
                    <Table.Header><Table.Row>
                      <Table.HeaderCell>Character</Table.HeaderCell>
                      <Table.HeaderCell>Tier</Table.HeaderCell>
                      <Table.HeaderCell>Lines</Table.HeaderCell>
                      <Table.HeaderCell title="Detected gender — drives Auto-cast. Click a cell to correct.">Gender</Table.HeaderCell>
                      <Table.HeaderCell>Detected archetype</Table.HeaderCell>
                      <Table.HeaderCell>Suggested style</Table.HeaderCell>
                      <Table.HeaderCell style={{ minWidth: 220 }}>Designed voice</Table.HeaderCell>
                      <Table.HeaderCell textAlign="center"
                        title="Build ONE voice for this character from its approved recordings (2 operations). Previews first — nothing is spent until you confirm.">
                        Pool
                      </Table.HeaderCell>
                      <Table.HeaderCell textAlign="center">Hear</Table.HeaderCell>
                    </Table.Row></Table.Header>
                    <Table.Body>
                      {pagedCast.map((c, i) => (
                        <React.Fragment key={c.uuid || i}>
                        <Table.Row>
                          <Table.Cell style={{ position: 'relative' }}>
                            {/* ⭐ READ THE LINES WHERE THE GENDER IS SET. The row carries counts, an
                                archetype and a synopsis, none of which answer "is this a man or a
                                woman, and does the text say so?" — and gender is what Auto-cast pins
                                the voice on. The dialogue itself was two tabs away in the Green
                                Room, so the decision was being made away from its evidence. */}
                            <button
                              type="button"
                              className="aai-chevron-toggle"
                              onClick={() => toggleLines(c.uuid)}
                              aria-expanded={linesOpen.includes(c.uuid)}
                              title={linesOpen.includes(c.uuid) ? 'Hide the lines' : `Read ${c.name}'s lines`}
                              style={{ marginRight: 6 }}
                            >
                              <Icon name={linesOpen.includes(c.uuid) ? 'angle down' : 'angle right'}
                                style={{ margin: 0, color: '#777' }} />
                            </button>
                            <strong>{c.name}</strong>
                            {c.primaryBookUuid && (
                              <span style={{ position: 'absolute', top: '0.35em', right: '0.4em' }}>
                                <CharacterContextTrigger
                                  bookUuid={c.primaryBookUuid}
                                  characterId={c.uuid}
                                  characterName={c.name}
                                  bookTitle={c.primaryBookTitle}
                                  title={`See ${c.name}'s lines & voice context from the book`}
                                  variant="inline"
                                />
                              </span>
                            )}
                            {c.aliases && c.aliases.length > 0 && (
                              <div style={{ fontSize: '0.8em', color: '#999', marginTop: 2 }} title={c.aliases.join(', ')}>
                                <Icon name="linkify" size="small" style={{ color: '#b0b0b0' }} />
                                also: {c.aliases.slice(0, 3).join(', ')}{c.aliases.length > 3 ? ` · +${c.aliases.length - 3}` : ''}
                              </div>
                            )}
                            {/*
                              ⭐ WHERE ITS LINES WENT (operator's request). A character emptied by
                              reassignment shows 0 lines and an empty voice dropdown — and so does one
                              that never spoke. The same hint already exists in Manage Cast; without it
                              here, the grid cannot tell a decision already taken from an artefact
                              nobody has looked at, and the row invites a voice it does not need.
                              Chips, never ", " — a speaker label in this corpus can contain a comma
                              ("friendly, masculine voice"), so a comma cannot delimit names.
                            */}
                            {c.reassignedTo?.length > 0 && (
                              <div style={{ fontSize: '0.8em', color: '#5d8aa8', marginTop: 2, display: 'inline-flex', flexWrap: 'wrap', gap: '0.25em', alignItems: 'center' }}>
                                <Icon name="exchange" size="small" style={{ color: '#8fb3c7' }} />
                                reassigned to:
                                {c.reassignedTo.map((m, i) => (
                                  <span key={`${m.name}-${i}`}
                                    title={`${m.lines} line${m.lines === 1 ? '' : 's'} moved to “${m.name}”`}
                                    style={{ background: '#eef4f8', border: '1px solid #d7e5ee', borderRadius: 3, padding: '0 0.35em', whiteSpace: 'nowrap' }}>
                                    {m.name}{m.lines > 1 ? ` (${m.lines})` : ''}
                                  </span>
                                ))}
                              </div>
                            )}
                          </Table.Cell>
                          <Table.Cell><Label size="mini" color={TIER_COLOR[c.tier] || 'grey'}>{c.tier}</Label></Table.Cell>
                          <Table.Cell>{c.lines}</Table.Cell>
                          <Table.Cell style={{ minWidth: 94, position: 'relative' }}>
                            <Dropdown
                              selection compact
                              options={GENDER_OPTIONS}
                              value={c.gender || 'unknown'}
                              onChange={(e, { value }) => value !== (c.gender || 'unknown') && changeSignature(c, { gender: value })}
                              title={c.signatureLocked ? 'Signature set by you — locked against re-derive' : 'Detected gender — click to correct; drives Auto-cast'}
                              style={{ fontSize: '.9rem' }}
                            />
                            {c.signatureLocked && (
                              <Icon name="lock" size="small" style={{ color: '#b0b0b0', margin: 0, position: 'absolute', top: 4, right: 4 }} title="You set this signature — a re-derive won't overwrite it" />
                            )}
                          </Table.Cell>
                          <Table.Cell style={{ minWidth: 160 }}>
                            <EditableArchetype value={c.archetype} onSave={(v) => changeSignature(c, { archetype: v })} />
                          </Table.Cell>
                          <Table.Cell>
                            <Dropdown
                              inline
                              search
                              scrolling
                              options={styleOptions}
                              value={c.styleKey || ''}
                              placeholder="—"
                              onChange={(e, { value }) => changeStyle(c, value)}
                            />
                          </Table.Cell>
                          <Table.Cell style={{ minWidth: 220 }}>
                            {c.designUuid ? (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35em' }}>
                                <Label size="mini" color="purple" title="renders with this designed voice">
                                  <Icon name="cogs" />{c.designName || 'design'}
                                </Label>
                                <Button icon compact basic size="mini" title="Detach designed voice"
                                  loading={designBusy === c.uuid} onClick={() => detachDesign(c)}>
                                  <Icon name="close" />
                                </Button>
                              </span>
                            ) : (
                              designOptions.length ? (
                                <Dropdown selection compact options={designOptions}
                                  value="" placeholder="attach…" selectOnBlur={false}
                                  disabled={designBusy === c.uuid}
                                  onChange={(e, { value }) => value && attachDesign(c, value)}
                                  style={{ minWidth: 200 }} />
                              ) : <span style={{ color: '#bbb', fontSize: '0.85em' }}>—</span>
                            )}
                          </Table.Cell>
                          <Table.Cell textAlign="center">
                            <Button
                              icon compact basic size="mini"
                              loading={poolBusy === c.uuid}
                              disabled={!!poolBusy}
                              onClick={() => previewPool(c)}
                              title={`Preview a pooled voice for ${c.name} — costs nothing`}
                            >
                              <Icon name="clone outline" />
                            </Button>
                          </Table.Cell>
                          <Table.Cell textAlign="center">
                            {c.hasVoice && c.lines > 0 && (
                              auditionUuid === c.uuid ? (
                                <Button.Group size="mini">
                                  {auditionLoading ? (
                                    <Button icon compact loading><Icon name="spinner" /></Button>
                                  ) : (
                                    <Button icon compact primary onClick={togglePauseAudition}
                                      title={auditionPaused ? 'Resume' : 'Pause'}>
                                      <Icon name={auditionPaused ? 'play' : 'pause'} />
                                    </Button>
                                  )}
                                  <Button icon compact basic color="red" onClick={cancelAudition} title="Cancel">
                                    <Icon name="stop" />
                                  </Button>
                                </Button.Group>
                              ) : (
                                <Button.Group size="mini">
                                  <Button
                                    icon compact primary
                                    disabled={!!auditionUuid || !!previewUuid}
                                    onClick={() => audition(c.uuid, c.name)}
                                    title={`Hear ${c.name} say their line`}
                                  >
                                    <Icon name="play" />
                                  </Button>
                                  <Button
                                    icon compact basic
                                    disabled={!c.previewUrl || !!auditionUuid}
                                    color={previewUuid === c.uuid ? 'teal' : undefined}
                                    onClick={() => c.previewUrl && previewVoice(c)}
                                    title={c.previewUrl ? 'Preview the voice (stock sample)' : 'No stock preview available for this voice'}
                                  >
                                    <Icon name={previewUuid === c.uuid ? 'stop' : 'headphones'} />
                                  </Button>
                                </Button.Group>
                              )
                            )}
                          </Table.Cell>
                        </Table.Row>
                        {/* ⚠️ A SECOND ROW, not a panel inside the first cell. A Semantic table lays
                            its cells out in a grid, so a wide block inside one cell widens that
                            COLUMN for every row. Spanning the full width keeps the table's shape and
                            gives the text the whole line to use.
                            ⓘ `q` is deliberately empty: the studio's search box filters the CAST,
                            while the panel's q windows a character's lines around matches. Passing
                            one into the other would silently narrow the evidence being read. */}
                        {linesOpen.includes(c.uuid) && (
                          <Table.Row>
                            <Table.Cell colSpan="9" style={{ background: '#fcfcfd' }}>
                              <CharacterLinesPanel
                                litGroupUuid={litGroupUuid}
                                characterUuid={c.uuid}
                              />
                            </Table.Cell>
                          </Table.Row>
                        )}
                        </React.Fragment>
                      ))}
                    </Table.Body>
                  </Table>
                </div>
                {castTotal === 0 && (
                  <Message info size="small" style={{ marginTop: '0.5em' }}>
                    <Icon name="search" /> No characters match these filters.
                  </Message>
                )}
                {totalCastPages > 1 && (
                  <div style={{ textAlign: 'center', marginTop: '0.8em' }}>
                    <Pagination
                      size="mini"
                      activePage={castPage}
                      totalPages={totalCastPages}
                      boundaryRange={1}
                      siblingRange={1}
                      ellipsisItem={null}
                      onPageChange={(e, { activePage }) => setCastPage(activePage)}
                    />
                  </div>
                )}
              </>
            )}
          </>
        )}
      </Container>

      {/* ── Pooled clone preview ──────────────────────────────────────────────────────────────
        * The dry run rendered as a decision, not a receipt. Everything here comes from a request
        * that wrote nothing and spent nothing; the only button that costs anything is Mint, and
        * it says what it costs. See services/voiceCloning/cloneSampleSelector.js for why there is
        * a budget at all — past ~3 minutes ElevenLabs say more audio "can be detrimental". */}
      <Modal open={!!poolPreview} onClose={() => setPoolPreview(null)} size="small" closeIcon>
        <Modal.Header>
          <Icon name="clone outline" /> Pooled voice — {poolPreview?.character?.name}
        </Modal.Header>
        <Modal.Content scrolling>
          {poolPreview && (
            <>
              <Message
                info={poolPreview.withinIdeal}
                warning={!poolPreview.withinIdeal}
                icon={poolPreview.withinIdeal ? 'check circle' : 'warning sign'}
                header={`${poolPreview.selected.length} recording${poolPreview.selected.length === 1 ? '' : 's'} · ${poolPreview.totalSeconds}s`}
                content={poolPreview.withinIdeal
                  ? 'Inside the 60-120s window ElevenLabs recommends for an instant clone.'
                  : (poolPreview.overCeiling
                    ? 'Over the 3-minute ceiling — ElevenLabs warn that this can degrade the clone.'
                    : 'Outside the recommended 60-120s window. The clone may be thin.')}
              />

              <Table basic="very" compact="very" size="small">
                <Table.Header><Table.Row>
                  <Table.HeaderCell>Emotion</Table.HeaderCell>
                  <Table.HeaderCell>Lines spoken</Table.HeaderCell>
                  <Table.HeaderCell>Seconds</Table.HeaderCell>
                </Table.Row></Table.Header>
                <Table.Body>
                  {poolPreview.selected.map((x) => (
                    <Table.Row key={x.emotion}>
                      <Table.Cell><Label size="mini" color="teal" horizontal>{x.emotion}</Label></Table.Cell>
                      <Table.Cell>{x.occurrenceCount}</Table.Cell>
                      <Table.Cell>{Number(x.seconds).toFixed(1)}s</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>

              {/* What was left OUT matters as much as what went in: these are the emotions the
                * voice will have to perform without ever having been heard performing them. */}
              {poolPreview.droppedForBudget?.length > 0 && (
                <p style={{ fontSize: '0.9em', color: '#888' }}>
                  <strong>{poolPreview.droppedForBudget.length} cut for budget:</strong>{' '}
                  {poolPreview.droppedForBudget.map((d) => d.emotion).join(', ')}
                </p>
              )}
              {poolPreview.skippedUnspoken?.length > 0 && (
                <p style={{ fontSize: '0.9em', color: '#aaa' }}>
                  {poolPreview.skippedUnspoken.length} never spoken by this character, so not sampled.
                </p>
              )}
              {poolPreview.notNormalized > 0 && (
                <p style={{ fontSize: '0.9em', color: '#a55' }}>
                  {poolPreview.notNormalized} approved recording(s) are not normalized yet and cannot be sent.
                </p>
              )}
              {poolPreview.notes?.map((n, i) => (
                <p key={i} style={{ fontSize: '0.85em', color: '#888' }}>{n}</p>
              ))}
            </>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button basic onClick={() => setPoolPreview(null)}>Cancel</Button>
          <Button primary loading={poolMinting} disabled={poolMinting} onClick={mintPool}>
            <Icon name="microphone" /> Mint this voice — 2 operations
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Result / error, shown after the modal closes so the outcome is not lost with it. */}
      <Modal open={!!poolResult} onClose={() => setPoolResult(null)} size="tiny" closeIcon>
        <Modal.Header><Icon name="check circle" color="green" /> Pooled voice queued</Modal.Header>
        <Modal.Content>
          {poolResult && (
            <p>
              <strong>{poolResult.characterName}</strong> — one voice from {poolResult.selected?.length}{' '}
              recording(s), {poolResult.totalSeconds}s. It is queued for export to ElevenLabs; the
              clone pipeline page shows its progress.
            </p>
          )}
        </Modal.Content>
        <Modal.Actions><Button onClick={() => setPoolResult(null)}>Close</Button></Modal.Actions>
      </Modal>

      <Modal open={!!poolError} onClose={() => setPoolError(null)} size="tiny" closeIcon>
        <Modal.Header>
          <Icon name={poolError?.notPoolable ? 'info circle' : 'warning sign'}
            color={poolError?.notPoolable ? 'blue' : 'red'} />
          {poolError?.notPoolable ? ' Not ready to pool' : ' Pooling failed'}
        </Modal.Header>
        <Modal.Content>
          <p><strong>{poolError?.character?.name}</strong></p>
          <p>{poolError?.message}</p>
          {poolError?.notPoolable && (
            <p style={{ color: '#888', fontSize: '0.9em' }}>
              A character can be pooled once it has approved recordings whose audio has been
              normalized, for emotions it actually speaks.
            </p>
          )}
        </Modal.Content>
        <Modal.Actions><Button onClick={() => setPoolError(null)}>Close</Button></Modal.Actions>
      </Modal>

      {AuthStdFooter()}
    </Container>
  );
};

export default TalentAgencyPage;
